import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useHistorySnapshots - save + undo/redo for design editors.
 *
 * Owner's requirement (2026-10-26): an edited design must be saveable
 * so it does NOT snap back to the old style on reload, and stepping
 * back must be an explicit Undo (with Redo alongside).
 *
 * How it works, editor-agnostically:
 * - The editor describes its design as a JSON snapshot (snapshot()),
 *   knows how to restore one (apply()), and lists the state values
 *   that signal a design change (watch).
 * - Changes are recorded (debounced) onto an undo stack; Undo/Redo
 *   walk the stack by calling apply().
 * - Save writes the current snapshot to localStorage under
 *   storageKey; on mount, a saved snapshot is restored automatically,
 *   so the saved design survives reloads and revisits.
 * - Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z / Ctrl+Y work outside text fields.
 */

export interface HistorySnapshotOptions {
    storageKey: string;
    snapshot: () => any;
    apply: (snap: any) => void;
    watch: unknown[];
    debounceMs?: number;
    historyLimit?: number;
    /** Return a smaller snapshot for storage when the full one is too big (e.g. embedded images). */
    stripForSave?: (snap: any) => any;
    /**
     * Editor kind for account-saved designs ('check', 'tax', 'globaldoc',
     * 'invoice'). Omit to keep saving browser-local only. When set and the
     * user is signed in, Save Design also writes the design to their
     * account on the server, so it follows them across devices - the
     * numbers inside the snapshot included.
     */
    designKind?: string;
    /** Human name for a new account design, read from the editor's current state. */
    designName?: () => string;
}

export interface AccountDesign {
    id: string;
    name: string;
    kind: string;
    created_at: string;
    updated_at: string;
}

export interface HistorySnapshots {
    undo: () => void;
    redo: () => void;
    save: () => 'full' | 'partial' | false;
    canUndo: boolean;
    canRedo: boolean;
    dirty: boolean;
    savedAt: number | null;
    saveNote: string | null;
    /** Account sync (present only when the editor set designKind). */
    accountEnabled: boolean;
    accountDesigns: AccountDesign[];
    accountId: string | null;
    accountBusy: boolean;
    saveToAccount: () => Promise<boolean>;
    loadAccountDesign: (id: string) => Promise<boolean>;
    deleteAccountDesign: (id: string) => Promise<void>;
    refreshAccountDesigns: () => Promise<void>;
    /** Apply an externally fetched snapshot as the current design. */
    restore: (snap: any) => void;
}

const authToken = (): string | null => {
    try { return localStorage.getItem('token'); } catch { return null; }
};

const ser = (v: any): string => {
    try { return JSON.stringify(v) ?? ''; } catch { return ''; }
};

export function useHistorySnapshots(opts: HistorySnapshotOptions): HistorySnapshots {
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const [saveNote, setSaveNote] = useState<string | null>(null);

    // ---- Account-saved designs (server sync) ----
    const [accountEnabled, setAccountEnabled] = useState<boolean>(() => Boolean(opts.designKind && authToken()));
    const [accountDesigns, setAccountDesigns] = useState<AccountDesign[]>([]);
    const [accountId, setAccountId] = useState<string | null>(null);
    const [accountBusy, setAccountBusy] = useState(false);

    const acctKey = () => `${optsRef.current.storageKey}:acct`;
    const setLinkedAccountId = useCallback((id: string | null) => {
        setAccountId(id);
        try {
            if (id) localStorage.setItem(acctKey(), id);
            else localStorage.removeItem(acctKey());
        } catch { /* storage unavailable */ }
    }, []);

    const refreshAccountDesigns = useCallback(async () => {
        const token = authToken();
        if (!optsRef.current.designKind || !token) return;
        try {
            const res = await fetch('/api/designs', { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.status === 401) { setAccountEnabled(false); return; }
            const json = await res.json().catch(() => null);
            if (json?.success && Array.isArray(json.designs)) {
                setAccountDesigns(json.designs.filter((d: AccountDesign) => d.kind === optsRef.current.designKind));
            }
        } catch { /* offline or server down: local save still works */ }
    }, []);

    // On mount: restore the linked account-design id and pull the list.
    useEffect(() => {
        if (!optsRef.current.designKind || !authToken()) return;
        try {
            const linked = localStorage.getItem(acctKey());
            if (linked) setAccountId(linked);
        } catch { /* ignore */ }
        refreshAccountDesigns();
    }, []);

    const optsRef = useRef(opts);
    optsRef.current = opts;
    const pastRef = useRef<any[]>([]);
    const futureRef = useRef<any[]>([]);
    const presentRef = useRef<any>(null);
    const applyingRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Mount: restore the saved design (if any) and seed the history with
    // whatever the editor then holds, so Undo can always return here.
    useEffect(() => {
        let initial = optsRef.current.snapshot();
        try {
            const raw = localStorage.getItem(optsRef.current.storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.state) {
                    applyingRef.current = true;
                    optsRef.current.apply(parsed.state);
                    initial = parsed.state;
                    if (parsed.at) setSavedAt(parsed.at);
                    // The watch effect's next pass treats the applied
                    // state as the present rather than a new edit.
                    setTimeout(() => { applyingRef.current = false; }, 1200);
                }
            }
        } catch { /* storage unavailable or corrupt: start from defaults */ }
        presentRef.current = initial;
    }, []);

    // Record design changes onto the undo stack (debounced so a drag or
    // a burst of typing becomes one step).
    useEffect(() => {
        // Sign-in can happen while the editor stays mounted (auth is a
        // modal over the studio). The moment a token exists, enable
        // account saving and pull the user's design list.
        if (optsRef.current.designKind && authToken() && !accountEnabled) {
            setAccountEnabled(true);
            refreshAccountDesigns();
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            const snap = optsRef.current.snapshot();
            if (applyingRef.current) {
                // This change came from apply() (restore/undo/redo):
                // adopt it as the present without recording a step.
                presentRef.current = snap;
                applyingRef.current = false;
                return;
            }
            if (presentRef.current == null) { presentRef.current = snap; return; }
            if (ser(snap) === ser(presentRef.current)) return;
            pastRef.current.push(presentRef.current);
            const limit = optsRef.current.historyLimit ?? 60;
            if (pastRef.current.length > limit) pastRef.current.shift();
            presentRef.current = snap;
            futureRef.current = [];
            setCanUndo(true);
            setCanRedo(false);
            setDirty(true);
        }, opts.debounceMs ?? 350);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, opts.watch);

    const applySnap = useCallback((snap: any) => {
        applyingRef.current = true;
        presentRef.current = snap;
        optsRef.current.apply(snap);
        setDirty(true);
        // Safety net: if the applied state produces no watch change
        // (a no-op apply), don't leave the recorder muted.
        setTimeout(() => { applyingRef.current = false; }, 1200);
    }, []);

    const undo = useCallback(() => {
        if (!pastRef.current.length) return;
        const prev = pastRef.current.pop();
        futureRef.current.push(presentRef.current);
        applySnap(prev);
        setCanUndo(pastRef.current.length > 0);
        setCanRedo(true);
    }, [applySnap]);

    const redo = useCallback(() => {
        if (!futureRef.current.length) return;
        const next = futureRef.current.pop();
        pastRef.current.push(presentRef.current);
        applySnap(next);
        setCanRedo(futureRef.current.length > 0);
        setCanUndo(true);
    }, [applySnap]);

    const save = useCallback((): 'full' | 'partial' | false => {
        const snap = optsRef.current.snapshot();
        presentRef.current = snap;
        const write = (value: any) => localStorage.setItem(
            optsRef.current.storageKey,
            JSON.stringify({ at: Date.now(), state: value })
        );
        try {
            write(snap);
            setSavedAt(Date.now());
            setDirty(false);
            setSaveNote(null);
            return 'full';
        } catch {
            // Quota: retry with the editor's stripped snapshot if it has one.
            if (optsRef.current.stripForSave) {
                try {
                    write(optsRef.current.stripForSave(snap));
                    setSavedAt(Date.now());
                    setDirty(false);
                    setSaveNote('Saved without embedded images (too large for browser storage)');
                    return 'partial';
                } catch { /* fall through */ }
            }
            setSaveNote('Save failed — browser storage is full or unavailable');
            return false;
        }
    }, []);

    const saveToAccount = useCallback(async (): Promise<boolean> => {
        const token = authToken();
        const kind = optsRef.current.designKind;
        if (!kind || !token) return false;
        // The user may have signed in after this editor mounted (the
        // enabled flag is first computed at mount): light it up now.
        setAccountEnabled(true);
        setAccountBusy(true);
        try {
            const snap = optsRef.current.snapshot();
            const data = optsRef.current.stripForSave ? optsRef.current.stripForSave(snap) : snap;
            const name = (optsRef.current.designName?.() || '').trim()
                || `${kind} design — ${new Date().toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
            let res: Response;
            if (accountId) {
                res = await fetch(`/api/designs/${accountId}`, { method: 'PUT', headers, body: JSON.stringify({ name, data }) });
                if (res.status === 404) {
                    // The linked design was deleted elsewhere: save as new.
                    res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name, kind, data }) });
                }
            } else {
                res = await fetch('/api/designs', { method: 'POST', headers, body: JSON.stringify({ name, kind, data }) });
            }
            if (res.status === 401) {
                setAccountEnabled(false);
                setSaveNote('Sign in to save designs to your account');
                return false;
            }
            const json = await res.json().catch(() => null);
            if (!res.ok || !json?.success) {
                setSaveNote(json?.error || 'Account save failed — design is saved in this browser only');
                return false;
            }
            if (json.design?.id) setLinkedAccountId(json.design.id);
            presentRef.current = snap;
            setSavedAt(Date.now());
            setDirty(false);
            setSaveNote(optsRef.current.stripForSave
                ? 'Saved to your account (without embedded images)'
                : 'Saved to your account');
            refreshAccountDesigns();
            return true;
        } catch {
            setSaveNote('Account save failed — design is saved in this browser only');
            return false;
        } finally {
            setAccountBusy(false);
        }
    }, [accountId, refreshAccountDesigns, setLinkedAccountId]);

    const loadAccountDesign = useCallback(async (id: string): Promise<boolean> => {
        const token = authToken();
        if (!token) return false;
        setAccountBusy(true);
        try {
            const res = await fetch(`/api/designs/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.status === 401) { setAccountEnabled(false); return false; }
            const json = await res.json().catch(() => null);
            if (!res.ok || !json?.success || !json.design?.data) {
                setSaveNote(json?.error || 'Could not load that design');
                return false;
            }
            applySnap(json.design.data);
            setLinkedAccountId(id);
            try {
                localStorage.setItem(
                    optsRef.current.storageKey,
                    JSON.stringify({ at: Date.now(), state: json.design.data })
                );
            } catch { /* the design is in the account regardless */ }
            setSavedAt(Date.now());
            setSaveNote(`Loaded "${json.design.name}" from your account`);
            return true;
        } catch {
            setSaveNote('Could not load that design');
            return false;
        } finally {
            setAccountBusy(false);
        }
    }, [applySnap, setLinkedAccountId]);

    const deleteAccountDesign = useCallback(async (id: string): Promise<void> => {
        const token = authToken();
        if (!token) return;
        setAccountBusy(true);
        try {
            await fetch(`/api/designs/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            if (id === accountId) setLinkedAccountId(null);
            setSaveNote('Design deleted from your account');
            refreshAccountDesigns();
        } catch {
            setSaveNote('Could not delete that design');
        } finally {
            setAccountBusy(false);
        }
    }, [accountId, refreshAccountDesigns, setLinkedAccountId]);

    // Keyboard: Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z / Ctrl+Y redo.
    // Skipped while typing so text fields keep their native undo.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
            if (!(e.ctrlKey || e.metaKey)) return;
            const key = e.key.toLowerCase();
            if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
            else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); redo(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo, redo]);

    return {
        undo, redo, save, canUndo, canRedo, dirty, savedAt, saveNote,
        accountEnabled, accountDesigns, accountId, accountBusy,
        saveToAccount, loadAccountDesign, deleteAccountDesign, refreshAccountDesigns,
        restore: applySnap
    };
}
